/** @format */

import { Chain, Environment } from '@shared/data-access/interfaces';
import { Observable, throwError } from 'rxjs';
import { Purchase, TotalPurchases } from '@my-account/data-access/interfaces';
import { catchError, map } from 'rxjs/operators';

import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { MyAccountAnalyticsService, Store, HttpService } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class PurchasesService {
	constructor(
		private chain: Chain,
		private environment: Environment,
		private httpService: HttpService,
		private store: Store,
		private myAccountAnalyticsService: MyAccountAnalyticsService
	) {}

	/**
	 * @description Gets most recent order numbers. Pass order numbers into TrackOrderService to get order details
	 * @param lastPurchaseIndex {number}.
	 * @param lastOrderHistoryIndex {number}.
	 * @param pageSize {number}.
	 * @param orderhistorytype {string} 'IN_STORE' | 'ONLINE' | 'COMBINATION'
	 * @returns
	 */
	getPurchaseHistory(
		lastPurchaseIndex = 0,
		lastOrderHistoryIndex = 0,
		pageSize = 10,
		orderhistorytype = ''
	): Observable<Purchase> {
		const url = `${this.environment.baseUrls.orderService}/v3/Purchases`;
		let params = new HttpParams()
			.set('lastPurchaseIndex', lastPurchaseIndex)
			.set('lastOrderHistoryIndex', lastOrderHistoryIndex)
			.set('pageSize', pageSize)
			.set('chain', this.chain.chainIdentifierAbbr);

		if (orderhistorytype) {
			params = params.append('orderhistorytype', orderhistorytype);
		}

		const httpOptions = {
			params: params,
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.get<Purchase>(url, 0, httpOptions).pipe(
			map((result) => {
				const purchaseType =
					orderhistorytype === 'IN_STORE' ? 'INSTORE_PURCHASES' : 'PURCHASES';
				this.store.set(purchaseType, result);
				return result;
			}),
			catchError((error) => {
				this.myAccountAnalyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_PURCHASES,
					'Failed to get purchases',
					error
				);
				return throwError(error);
			})
		);
	}

	getTotal(): Observable<TotalPurchases> {
		return this.httpService
			.get<TotalPurchases>(
				`${this.environment.baseUrls.orderService}/v2/Purchases/total?chain=${this.chain.chainIdentifierAbbr}`,
				0,
				{ withCredentials: true, useApiAuth: true }
			)
			.pipe(
				catchError((error) => {
					this.myAccountAnalyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_PURCHASES,
						'Failed to get total purchases',
						error
					);
					return throwError(error);
				})
			);
	}

	sendOrderConfirmationEmail(orderNumber: string, email: string): Observable<unknown> {
		const url = `${this.environment.baseUrls.orderService}/v3/orders/${orderNumber}/orderConfirmation`;
		return this.httpService
			.put(url, { emailAddress: email }, { withCredentials: true, useApiAuth: true })
			.pipe(
				catchError((error) => {
					this.myAccountAnalyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_PURCHASES,
						'Failed to update order confirmation',
						error
					);
					return throwError(error);
				})
			);
	}
}
